import React from "react";
import { motion } from "framer-motion";
import { trackLead } from "../../lib/track";

const TELEGRAM_URL = "https://t.me/+cr1LhxIMB-5jODJl";
const LOGO_URL = "/assets/logo.png";

const ease = [0.2, 0.7, 0.2, 1];

const Hero = () => {
  return (
    <section
      id="top"
      className="relative isolate min-h-[100svh] w-full overflow-hidden pt-[64px] md:pt-[72px] flex items-center"
      data-testid="hero-section"
    >
      {/* Atmospheric layers */}
      <div className="absolute inset-0 -z-10">
        {/* Top vignette */}
        <div className="absolute inset-x-0 top-0 h-[60%] bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(78,168,255,0.18),transparent_60%)]" />
        {/* Soft blue blobs */}
        <div className="glow-blob top-[10%] left-[8%] h-[420px] w-[420px] bg-[rgba(31,111,224,0.22)]" />
        <div className="glow-blob top-[20%] right-[6%] h-[360px] w-[360px] bg-[rgba(78,168,255,0.16)]" />

        {/* Giant horizon arc */}
        <div className="horizon-arc anim-pulse-soft" />
        <div className="horizon-line" />

        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,black,transparent_75%)]" />

        {/* Floating particles */}
        {Array.from({ length: 22 }).map((_, i) => (
          <span
            key={i}
            className="absolute block rounded-full bg-white anim-pulse-soft"
            style={{
              top: `${(i * 53) % 100}%`,
              left: `${(i * 37) % 100}%`,
              width: `${1 + (i % 3)}px`,
              height: `${1 + (i % 3)}px`,
              opacity: 0.15 + ((i % 5) / 10),
              animationDelay: `${(i % 6) * 0.6}s`,
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-[1200px] px-5 md:px-10 pt-4 sm:pt-6 md:pt-10 pb-10">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="flex justify-center"
        >
          <span className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-[10.5px] sm:text-[11px] tracking-[0.26em] sm:tracking-[0.28em] uppercase text-white/70 backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-[#4ea8ff] shadow-[0_0_12px_#4ea8ff] anim-pulse-soft" />
            AI · Quotex Binary Engine
          </span>
        </motion.div>

        {/* Glossy orb with logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease, delay: 0.1 }}
          className="relative mx-auto mt-6 sm:mt-7 md:mt-9 h-[190px] w-[190px] sm:h-[220px] sm:w-[220px] md:h-[260px] md:w-[260px]"
          data-testid="hero-orb"
        >
          {/* Outer glow rings */}
          <div className="absolute inset-[-50px] rounded-full bg-[radial-gradient(circle,rgba(78,168,255,0.35),transparent_60%)] blur-2xl anim-pulse-soft" />
          <div className="absolute inset-[-18px] rounded-full border border-white/[0.06] anim-rotate-slow" />
          <div className="absolute inset-[-36px] rounded-full border border-white/[0.04] anim-rotate-slow" style={{ animationDirection: "reverse" }} />

          {/* Orb body */}
          <div className="anim-float relative h-full w-full rounded-full overflow-hidden ring-glow-blue">
            <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.18),transparent_45%),radial-gradient(circle_at_70%_80%,rgba(31,111,224,0.45),transparent_55%),linear-gradient(180deg,#0a1530_0%,#04060a_100%)]" />
            {/* Reflection sweep */}
            <div className="absolute inset-0 rounded-full anim-sheen overflow-hidden" />
            {/* Logo — zoomed on bull mark via background positioning (precise crop) */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                backgroundImage: `url(${LOGO_URL})`,
                backgroundSize: "180%",
                backgroundPosition: "center 22%",
                backgroundRepeat: "no-repeat",
                filter: "drop-shadow(0 0 28px rgba(78,168,255,0.55))",
              }}
              aria-label="BIGBULL Official Logo"
              data-testid="hero-logo"
            />
            {/* Glossy top reflection */}
            <div className="absolute top-0 left-0 right-0 h-1/2 rounded-t-full bg-[linear-gradient(180deg,rgba(255,255,255,0.14),transparent_70%)] pointer-events-none" />
            {/* Inner ring */}
            <div className="absolute inset-0 rounded-full ring-1 ring-inset ring-white/10 pointer-events-none" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.25 }}
          className="font-display mt-7 sm:mt-8 md:mt-10 text-center text-[40px] sm:text-[60px] md:text-[84px] lg:text-[100px] leading-[0.95] tracking-[-0.04em] font-bold text-balance"
          data-testid="hero-title"
        >
          <span className="text-gradient-silver">BIGBULL</span>{" "}
          <span className="text-gradient-blue">AI BOT</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.4 }}
          className="mt-4 sm:mt-5 text-center text-[16px] sm:text-[18px] md:text-[22px] text-white/75 font-body font-light max-w-[640px] mx-auto text-balance"
          data-testid="hero-subtitle"
        >
          AI-powered OTC binary trading system.
        </motion.p>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease, delay: 0.55 }}
          className="mt-2.5 sm:mt-3 text-center text-[11px] sm:text-[12.5px] tracking-[0.3em] uppercase text-white/40 font-mono-luxe"
        >
          Quotex Signals · OTC Entries · Structured Compounding
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.7 }}
          className="mt-6 sm:mt-7 md:mt-9 flex flex-col sm:flex-row items-center justify-center gap-3.5 sm:gap-4"
        >
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackLead("get_free_access")}
            className="btn-pill btn-primary-blue w-full sm:w-auto"
            data-testid="hero-cta-primary"
          >
            Get Free Access
            <span aria-hidden>→</span>
          </a>
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackLead("watch_demo")}
            className="btn-pill btn-secondary-glass w-full sm:w-auto"
            data-testid="hero-cta-secondary"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/10">
              <svg width="9" height="10" viewBox="0 0 9 10" fill="none">
                <path d="M0.5 0.5L8 5L0.5 9.5V0.5Z" fill="currentColor" />
              </svg>
            </span>
            Watch Demo
          </a>
        </motion.div>

        {/* Micro trust strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.95 }}
          className="mt-7 sm:mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[10.5px] sm:text-[11px] tracking-[0.3em] uppercase text-white/35"
        >
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-[#4ea8ff]" /> Quotex Ready
          </span>
          <span className="hidden sm:inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-[#4ea8ff]" /> 82% Avg Win-Rate
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-[#4ea8ff]" /> Telegram Delivery
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
